import React from 'react';
import { Paper, Typography, Button, Box } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import {
  white,
  gray80,
  gray40,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { useNavigate } from 'react-router-dom';

const styles = {
  paper: {
    padding: '40px 40px',
    maxWidth: '398px',
    width: '100%',
    backgroundColor: white,
    border: `1px solid ${gray40}`,
    textAlign: 'center',
    // SP（960px 以下）でカードの左右に余白を作る。
    // ブレークポイントは design-rules.md の SP 判定（960px）に揃える。
    // このプロジェクトは CssBaseline を使っておらず box-sizing は既定の content-box のため、
    // width の指定に padding(80px) と border(2px) が加算される。border-box に切り替えないと
    // カードが親幅を超え、中央寄せで左右にはみ出してマージンが見えなくなる。
    // PC 側の見た目を変えないよう、切り替えは SP のみに閉じている。
    '@media (max-width: 960px)': {
      boxSizing: 'border-box',
      width: 'calc(100% - 32px)',
      margin: '0 16px',
    },
  },
  icon: {
    fontSize: '56px',
    color: gray80,
    marginBottom: '24px',
  },
  button: {
    marginTop: '30px',
    fontWeight: '700',
  },
  beforeNextButton: {
    marginTop: '16px',
    width: '100%',
  },
  titleText: {
    fontSize: '22px',
    fontWeight: '400',
    marginBottom: '10px',
    whiteSpace: 'pre-wrap',
  },
  messageText: {
    textAlign: 'left',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
  },
  messageTextCenter: {
    textAlign: 'center',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
  },
};

interface CompleteProps {
  IconComponent: SvgIconComponent;
  iconColor?: string;
  titleText: string;
  titleStyle?: React.CSSProperties;
  isMessageCenter?: boolean;
  messageText: string;
  buttonText: string;
  targetPath: string;
  secondaryLeftButtonText?: string;
  secondaryLeftPath?: {
    path: string;
    state?: { [key: string]: string | number | boolean | null };
  } | null;
  secondaryRightButtonText?: string;
  secondaryRightPath?: {
    path: string;
    state: { [key: string]: string | number | boolean | null };
  } | null;
}

const MypageCompleteCard: React.FC<CompleteProps> = ({
  IconComponent,
  iconColor,
  titleText,
  titleStyle,
  isMessageCenter,
  messageText,
  buttonText,
  targetPath,
  secondaryLeftButtonText,
  secondaryLeftPath,
  secondaryRightButtonText,
  secondaryRightPath,
}) => {
  const navigate = useNavigate();
  const hasThreeButtons = secondaryLeftButtonText && secondaryLeftPath;

  return (
    <>
      <Paper elevation={0} sx={styles.paper}>
        <IconComponent sx={{ ...styles.icon, color: iconColor || gray80 }} />
        <Typography variant="body2" gutterBottom sx={{ ...styles.titleText, ...titleStyle }}>
          {titleText}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={isMessageCenter ? styles.messageTextCenter : styles.messageText}
        >
          {messageText}
        </Typography>
        <Button
          onClick={(): void => {
            navigate(targetPath);
          }}
          fullWidth
          variant="outlined"
          sx={[
            hasThreeButtons ? tertiaryButtonBackgroundColor : secondaryButtonBackgroundColor,
            styles.button,
          ]}
        >
          {buttonText}
        </Button>
        {(secondaryLeftButtonText && secondaryLeftPath) ||
        (secondaryRightButtonText && secondaryRightPath) ? (
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {secondaryLeftButtonText && secondaryLeftPath && (
              <Button
                onClick={(): void => {
                  navigate(secondaryLeftPath.path, { state: secondaryLeftPath.state });
                }}
                variant="outlined"
                sx={[secondaryButtonBackgroundColor, styles.beforeNextButton]}
              >
                {secondaryLeftButtonText}
              </Button>
            )}
            {secondaryRightButtonText && secondaryRightPath && (
              <Button
                onClick={(): void => {
                  navigate(secondaryRightPath.path, { state: secondaryRightPath.state });
                }}
                variant="outlined"
                sx={[secondaryButtonBackgroundColor, styles.beforeNextButton]}
              >
                {secondaryRightButtonText}
              </Button>
            )}
          </Box>
        ) : null}
      </Paper>
    </>
  );
};

export default MypageCompleteCard;
